import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import fileToBase64 from "@/utils/fileToBase64";
import { handleApiError } from "@/utils/errorHandler";
import { updateBusiness } from "@/_actions/businessActions";
import AddIcon from "../img/add.svg";
import CameraIcon from "../img/camera.svg";
import { errorClassName, outlineButtonClassName, primaryButtonClassName } from "../styles";

const caption = "Imagen principal de tu página (tamaño recomendado 1024X278px formato .jpg o .png)";

// Mirrors the backend contract: JPG or PNG up to CLIENT_HERO_IMAGE_MAX_BYTES (5 MiB).
export const HERO_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const HERO_IMAGE_FORMATS = { "image/jpeg": "jpg", "image/png": "png" };
const UNCONFIRMED_UPLOAD_MESSAGE =
  "No pudimos confirmar si el banner se guardó. Revisa la vista previa e intenta de nuevo si no cambió.";
const UNCONFIRMED_REMOVAL_MESSAGE =
  "No pudimos confirmar si el banner se quitó. Revisa la vista previa e intenta de nuevo si sigue visible.";

function firstMessage(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return firstMessage(value[0]);
  if (value && typeof value === "object") return firstMessage(Object.values(value)[0]);
  return "";
}

function bannerErrorMessage(err, unconfirmedMessage) {
  const { status, data } = err.response ?? {};
  // Without a response (or after a proxy timeout) the backend may still have applied the change.
  if (!err.response || status === 504) return unconfirmedMessage;
  if (status === 413) return "La imagen es demasiado pesada. Usa una de máximo 5 MB.";
  if (status === 401) return handleApiError(err).userMessage;
  const apiMessage =
    firstMessage(data?.hero_image) || (typeof data?.detail === "string" ? data.detail : "");
  return apiMessage || handleApiError(err).userMessage;
}

// Without canEdit (owner/admin with cyt_landing) the banner is only shown, never offered for upload.
export default function UploadBanner({
  clientId,
  heroImage,
  canEdit = false,
  onUploaded,
  onUploadFailed,
  onRemoved,
  onRemoveFailed,
}) {
  const inputRef = useRef(null);
  const addButtonRef = useRef(null);
  // Shared by upload and removal so the banner is never replaced and removed at the same time.
  const actionInFlight = useRef(false);
  const focusAddAfterRemoval = useRef(false);
  // Stops the dialog from returning focus to its (now removed) trigger; reset on the next open.
  const skipDialogFocusReturn = useRef(false);
  const [failedImage, setFailedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");
  const imageFailed = Boolean(heroImage) && failedImage === heroImage;
  const isBusy = isUploading || isRemoving;
  // Requests need the real agency id; profile data may still be loading.
  const canSubmit = Boolean(clientId);

  useEffect(() => {
    // The "Quitar banner" trigger disappears after removal, so keyboard focus moves to "Agregar".
    if (!heroImage && focusAddAfterRemoval.current) {
      focusAddAfterRemoval.current = false;
      addButtonRef.current?.focus();
    }
  }, [heroImage]);

  const uploadBanner = async (event) => {
    const file = event.target.files?.[0];
    // Lets the same file be selected again after an error.
    event.target.value = "";
    if (!file || !canSubmit || actionInFlight.current) return;

    const format = HERO_IMAGE_FORMATS[file.type];
    if (!format) {
      setError("El banner debe ser una imagen JPG o PNG.");
      return;
    }
    if (file.size > HERO_IMAGE_MAX_BYTES) {
      setError("El banner no debe pesar más de 5 MB.");
      return;
    }

    actionInFlight.current = true;
    setIsUploading(true);
    setError("");
    try {
      let base64;
      try {
        const dataUrl = await fileToBase64(file);
        base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
      } catch {
        setError("No pudimos leer la imagen. Intenta con otro archivo.");
        return;
      }

      try {
        const response = await updateBusiness(clientId, {
          hero_image: { format, content_type: file.type, base64 },
        });
        if (typeof response?.hero_image !== "string" || !response.hero_image) {
          setError(UNCONFIRMED_UPLOAD_MESSAGE);
          onUploadFailed?.();
          return;
        }
        onUploaded?.(response.hero_image);
      } catch (err) {
        setError(bannerErrorMessage(err, UNCONFIRMED_UPLOAD_MESSAGE));
        onUploadFailed?.();
      }
    } finally {
      actionInFlight.current = false;
      setIsUploading(false);
    }
  };

  const removeBanner = async () => {
    if (!canSubmit || actionInFlight.current) return;

    actionInFlight.current = true;
    setIsRemoving(true);
    setError("");
    try {
      const response = await updateBusiness(clientId, { hero_image: null });
      // Only an explicit `hero_image: null` confirms the removal; a bare 200 does not.
      if (!response || !("hero_image" in response) || response.hero_image !== null) {
        setError(UNCONFIRMED_REMOVAL_MESSAGE);
        onRemoveFailed?.();
        return;
      }
      focusAddAfterRemoval.current = true;
      skipDialogFocusReturn.current = true;
      onRemoved?.();
    } catch (err) {
      setError(bannerErrorMessage(err, UNCONFIRMED_REMOVAL_MESSAGE));
      onRemoveFailed?.();
    } finally {
      actionInFlight.current = false;
      setIsRemoving(false);
      setConfirmOpen(false);
    }
  };

  if (!canEdit && (!heroImage || imageFailed)) return null;

  const selectFile = () => inputRef.current?.click();
  const fileInput = canEdit ? (
    <input
      ref={inputRef}
      id="banner-upload"
      name="banner-upload"
      type="file"
      className="hidden"
      accept="image/jpeg,image/png"
      onChange={uploadBanner}
    />
  ) : null;
  const errorMessage = error ? (
    <p role="alert" className={errorClassName}>
      {error}
    </p>
  ) : null;

  if (!heroImage) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-[#7b7b7b]">{caption}</p>
        <div>
          <Button
            ref={addButtonRef}
            type="button"
            disabled={isBusy || !canSubmit}
            onClick={selectFile}
            className={`${primaryButtonClassName} h-auto min-h-10 whitespace-normal py-2 text-left`}
          >
            <img src={AddIcon} alt="" width="24" height="24" />
            {isUploading ? "Subiendo banner…" : "Agregar Banner a página de inmobiliaria"}
          </Button>
          {errorMessage}
        </div>
        {fileInput}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="relative w-full max-w-[1024px]">
          <div className="aspect-[1024/278] w-full overflow-hidden bg-[#ecf1f1]">
            {imageFailed ? (
              <p className="flex size-full items-center justify-center p-4 text-center text-sm text-[#7b7b7b]">
                No pudimos mostrar el banner actual.
              </p>
            ) : (
              <img
                src={heroImage}
                alt="Banner de la página de tu inmobiliaria"
                className="size-full object-cover"
                onError={() => setFailedImage(heroImage)}
              />
            )}
          </div>
          {canEdit ? (
            // Over the image on desktop (Figma); below it on mobile so the short banner stays visible.
            <div className="mt-2 flex flex-wrap gap-2 md:absolute md:top-4 md:right-4 md:mt-0">
              <Button
                type="button"
                variant="outline"
                aria-label={isUploading ? "Subiendo banner" : "Editar banner"}
                disabled={isBusy || !canSubmit}
                onClick={selectFile}
                className={outlineButtonClassName}
              >
                <img src={CameraIcon} alt="" width="24" height="24" />
                {isUploading ? "Subiendo…" : "Editar"}
              </Button>
              <AlertDialog
                open={confirmOpen}
                onOpenChange={(open) => {
                  if (isRemoving) return;
                  if (open) skipDialogFocusReturn.current = false;
                  setConfirmOpen(open);
                }}
              >
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isBusy || !canSubmit}
                    className={outlineButtonClassName}
                  >
                    Quitar banner
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent
                  onCloseAutoFocus={(event) => {
                    if (skipDialogFocusReturn.current) event.preventDefault();
                  }}
                >
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Quitar el banner de la inmobiliaria?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Tu página dejará de mostrar esta imagen. Podrás agregar un nuevo banner cuando
                      quieras.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isRemoving}>Cancelar</AlertDialogCancel>
                    {/* A plain button keeps the dialog open while the request is pending. */}
                    <Button
                      type="button"
                      disabled={isRemoving}
                      onClick={removeBanner}
                      className={primaryButtonClassName}
                    >
                      {isRemoving ? "Quitando…" : "Quitar banner"}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : null}
        </div>
        {errorMessage}
      </div>
      {canEdit ? <p className="text-sm text-[#7b7b7b]">{caption}</p> : null}
      {fileInput}
    </div>
  );
}
